import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useE2Client } from 'modules/E2ClientProvider';
import { useUser } from 'modules/User';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { isProductionDomain } from 'utils/environment';

import { PhishingShareMethod } from './useDetectPhishing.config';

// Flag to check if we already checked phishing in this session
let checkedPhishingInCurrentSession = false;

/**
 * Hook to trigger spoof check of a design when it is shared
 * Returns method to trigger /detectPhishing endpoint with appropriate params
 * If spoof check has already been performed in this session, it will not be performed again
 *
 * @returns detectPhishing: (shareMethod: PhishingShareMethod) => Promise<void>
 */
export const useDetectPhishing = () => {
  const { venngageE2 } = useVenngageE1Gate();
  const { client, infographId: e2DesignId } = useE2Client();
  const { id: e1UUId } = useParams();
  const { email } = useUser();

  const privateLinkId = venngageE2?.privateLinkId;

  const detectPhishing = useCallback(
    async (shareMethod: PhishingShareMethod) => {
      // Only for production
      if (!isProductionDomain) return;

      // Already performed spoof check during this session, don't need to do again
      if (checkedPhishingInCurrentSession) return;
      if (!client) return;

      try {
        await client.detectPhishing({
          userEmail: email || '',
          designId: e2DesignId || '',
          e1UUId: e1UUId || '',
          designUrl: window.location.href,
          shareMethod,
          shareLink: `${window.location.protocol}//${window.location.host}/pl/${privateLinkId}`,
        });

        checkedPhishingInCurrentSession = true;
      } catch (error) {
        console.error(error);
      }
    },
    [email, e2DesignId, e1UUId, privateLinkId, client],
  );

  return { detectPhishing };
};
